<template>
  <ModalCard
    v-if="isOpen"
    :title="`Batch Edit ${getHeadingNameLabel(modelName)}`"
    :handle-close="() => $emit('update:isOpen', false)"
    width="80%"
  >
    <template #body-header>
      <div>
        <p>
          Download a spreadsheet filled with your items information. Modify the
          item information locally on the downloaded spreadsheet. Upload the
          spreadsheet containing the modifications using the
          <em>Batch Edit With Template</em>
          on list pages
        </p>
        <ol class="p-4 pl-5">
          <li class="px-3">
            Chose whether to add
            <b>all</b>
            or only
            <b>selected</b>
            items to the spreadseet,
          </li>
          <li class="px-3">
            Chose whether to add
            <b>attributes columns</b>
            to the spreadsheet. You can safely omit these columns if you just
            need to update annotations,
          </li>
          <li class="px-3">
            Chose which annotations to add in the spreadsheet.
          </li>
        </ol>
        <div class="help">
          <p class="pr-1">Note:</p>
          <ul class="pl-5" style="list-style-type: disc">
            <li>
              <b>Only batch editable attributes/annotations are exported</b>
              . If you want to
              <em>export</em>
              items, please use the export feature instead
            </li>
            <li>
              Download the spreadsheet template and modify the information,
            </li>
            <li>
              <b>
                <span class="icon is-small has-text-warning">
                  <i class="fa-solid fa-exclamation-triangle fa-sm" />
                </span>
                Removing annotations
              </b>
              <ul class="pl-5" style="list-style-type: disc">
                <li>
                  Removing the cell value for an annotation
                  <b class="has-text-danger">
                    will remove the annotation from the item
                  </b>
                </li>
                <li>
                  Removing the entire annotation column (incl. columns headers)
                  will result in this column being ignored when the spreadsheet
                  is re-uploaded. This means the values will be kept intact.
                </li>
                <li>
                  Remove any column you do not want to modify (except the ID
                  column necessary for identification)
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #body>
      <fieldset :disabled="isDownloading">
        <div
          v-if="exportSelected === true && selectedNonEditableRecordsCount > 0"
          class="batchedit-non-editable box py-1 px-2"
        >
          <div
            class="is-flex is-flex-direction-column is-justify-content-center is-align-items center"
          >
            <div class="is-flex is-justify-content-space-between p-3">
              <div class="editables-count">
                <span
                  v-tooltip="
                    `Make sure you obtain change permissions on non editable items before submitting the edition template`
                  "
                  class="icon hoverable-icon has-text-warning pr-4"
                >
                  <i class="fa-solid fa-exclamation-triangle" />
                </span>
                <b>
                  {{ selectedNonEditableRecordsCount }}
                  items are not editable
                </b>
              </div>
              <div class="editables-show-list">
                <a
                  class="stocks-link is-size-7"
                  :class="[viewItems === false ? 'has-text-weight-bold' : '']"
                  @click.prevent="viewItems = !viewItems"
                >
                  <span class="icon">
                    <i
                      class="fa-solid"
                      :class="[
                        viewItems === false ? 'fa-caret-down' : 'fa-caret-up',
                      ]"
                    />
                  </span>
                  {{ viewItems === false ? 'Show details' : 'Hide details' }}
                </a>
              </div>
            </div>
            <div
              v-show="viewItems"
              class="item-list-container"
              style="width: 100%; max-height: 250px; overflow: auto"
            >
              <div class="item-list">
                <div
                  v-for="({ id, name }, recordIndex) in selectedRecords"
                  :key="`${recordIndex}${id.value}`"
                  class="list-item is-flex is-justify-content-space-between"
                  :class="[
                    selectedNonEditableRecordIds.includes(id.value)
                      ? 'has-text-danger has-background-warning-light'
                      : 'has-text-success has-background-success-light',
                  ]"
                  style="padding: 0.3rem 0.5rem"
                >
                  <div
                    class="item-name long-text-ellipsis"
                    style="margin-right: 0.5rem"
                  >
                    {{ name.value.name }}
                  </div>
                  <div
                    v-tooltip="`You can't edit that item`"
                    class="item-editable-icon"
                  >
                    <span class="icon has-text-danger">
                      <i class="fa-solid fa-pen fa-xs" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="exportSelected === false">
          <div
            class="notification is-fullwidth mx-0"
            :class="
              selectedNonEditableRecordsCount >= 1 ? 'is-warning' : 'is-info'
            "
          >
            <span
              class="icon"
              :class="
                !selectedNonEditableRecordsCount >= 1 && 'has-text-primary'
              "
            >
              <i class="fa-solid fa-info-circle" />
            </span>
            <div class="text" style="font-size: 0.9em">
              Make sure you have change permission on every item you attempt to
              modify before submitting the edition template
            </div>
          </div>
        </div>
        <div class="is-flex is-align-items-center py-2">
          <div class="title is-size-6" style="margin: 0">
            <span class="icon">
              <i class="fa-solid fa-list-ul" />
            </span>
            Items
            <span class="has-text-weight-light">(rows)</span>
          </div>
          <div style="display: flex; align-items: center; margin-left: auto">
            <span
              v-tooltip="{
                content: `
                When selecting <code>all</code>, the filters set on the
                data (columns filters, global search) apply. Only the
                filtered record will be exported`,
                html: true,
              }"
              class="icon hoverable-icon has-text-info mr-2"
            >
              <i class="fa-solid fa-info-circle" />
            </span>
            <div class="buttons has-addons">
              <button
                v-tooltip="{
                  content:
                    selectedRecordsCount === 0
                      ? 'Select rows in table to use this option'
                      : undefined,
                }"
                class="button is-small"
                :disabled="selectedRecordsCount < 1"
                :class="{ 'is-primary is-selected': exportSelected }"
                style="width: 100px"
                @click="exportSelected = true"
              >
                Selected ({{ selectedRecordsCount }})
              </button>
              <button
                class="button is-small"
                :class="{
                  'is-primary is-selected': !exportSelected,
                }"
                :disabled="allRecords.count <= 0"
                style="width: 100px"
                @click="exportSelected = false"
              >
                All ({{ allRecords.count }})
              </button>
            </div>
          </div>
        </div>
        <div
          class="is-flex is-align-items-center is-justify-content-space-between py-2"
        >
          <div class="is-flex is-align-items-center is-flex-wrap-wrap">
            <div class="title is-size-6 pr-3 mb-0">
              <span class="icon">
                <i class="fa-solid fa-columns" />
              </span>
              Attributes of items
              <span class="has-text-weight-light">(columns)</span>
            </div>
            <div class="help">Include item attributes in the template ?</div>
          </div>
          <div class="is-flex is-align-items-center">
            <span
              v-tooltip="{
                content: `
                <p><code>Yes</code>: All batch editable properties and their
                values will be exported to the template</p>
                <p><code>No</code>: Only the name and ID columns are exported</p>
              `,
                html: true,
              }"
              class="icon hoverable-icon has-text-info mr-2"
            >
              <i class="fa-solid fa-info-circle" />
            </span>
            <div class="buttons has-addons is-flex-wrap-nowrap">
              <button
                class="button is-small"
                :class="{ 'is-primary is-selected': exportAttributes }"
                style="width: 100px"
                @click="exportAttributes = true"
              >
                Yes
              </button>
              <button
                class="button is-small"
                :class="{ 'is-primary is-selected': !exportAttributes }"
                style="width: 100px"
                @click="exportAttributes = false"
              >
                No
              </button>
            </div>
          </div>
        </div>
        <div class="is-flex is-align-items-center py-2">
          <div>
            <div class="title is-size-6 mb-1">
              <span class="icon">
                <i class="fa-solid fa-tags" />
              </span>
              Annotations of items
              <span class="has-text-weight-light">(columns)</span>
            </div>
            <div class="help pl-5">
              <p>
                By default, we have preselected
                <a
                  v-tooltip="
                    `Annotations for which at least one of the considered
                    items has a value`
                  "
                >
                  <b>
                    relevant annotations
                    <span class="icon is-small has-text-primary">
                      <i class="fa-solid fa-question fa-sm" />
                    </span>
                  </b>
                </a>
                for the considered list of items in the dropdown below. Please
                add additional annotations for which you wish to add values to.
              </p>
            </div>
          </div>
        </div>
        <div class="is-flex is-align-items-center py-2">
          <SelectContainer
            v-model="exportedAnnotations"
            placeholder="Search for annotations"
            track-by="value"
            :options="annotations"
            :multiple="true"
            :taggable="false"
            :disabled="isLoadingRelevantAnnotations"
            :loading="isLoadingRelevantAnnotations"
            :close-on-select="false"
            style="width: 100%"
          />
          <button
            v-tooltip="'Reinitialised the list of relevant annotations'"
            class="button is-small is-primary is-outlined ml-3"
            @click="setExportedAnnotations"
          >
            <span class="icon">
              <i class="fa-solid fa-undo" />
            </span>
          </button>
        </div>
      </fieldset>
    </template>

    <template #footer>
      <div class="is-flex is-flex-direction-column">
        <p
          v-if="
            (exportSelected === true && selectedRecords.length > 99) ||
            exportSelected === false
          "
          class="help mb-4"
        >
          The generation of the spreadseet document may take several minutes
          when downloading many items
        </p>
        <BaseButton
          label="Download"
          size="small"
          variant="primary"
          :outlined="true"
          :icon="['fa-solid', 'fa-download']"
          :disabled="!canDownload"
          :loading="isDownloading"
          style="margin-left: auto"
          @click="handleExport"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { getHeadingNameLabel } from '@/utils'
import { downloadFile } from '@/utils/api'
import SelectContainer from '../SelectContainer.vue'

export default {
  name: 'BatchEditGetTemplateModal',
  components: {
    SelectContainer,
  },
  props: {
    isOpen: Boolean,
    modelName: String,
    url: String,
    selectedRecords: {
      type: Array,
      default: () => [],
    },
    allRecords: {
      type: Object,
      default: () => {
        return {
          count: 0,
          queryFilters: {},
        }
      },
    },
    selectedRecordsAnnotations: {
      type: Array,
      default: null,
    },
    annotations: {
      type: Array,
      default: () => [],
    },
    relevantAnnotations: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      exportSelected: false,
      exportAttributes: true,
      exportedAnnotations: null,
      isDownloading: false,
      viewItems: false,
    }
  },
  computed: {
    isLoadingRelevantAnnotations() {
      return this.relevantAnnotations === null
    },
    selectedRecordIds() {
      return this.selectedRecords.map((r) => r.id?.value)
    },
    selectedRecordsCount() {
      return this.selectedRecords.length
    },
    selectedNonEditableRecords() {
      return this.selectedRecords.filter(
        ({ permissions }) => !this.hasChangePermission({ permissions }),
      )
    },
    selectedNonEditableRecordIds() {
      return this.selectedNonEditableRecords.map(({ id }) => id.value)
    },
    selectedNonEditableRecordsCount() {
      return this.selectedNonEditableRecordIds.length
    },
    hasRecordsToEdit() {
      return this.exportSelected
        ? this.selectedRecordsCount >= 1
        : this.allRecords.count >= 1
    },
    canDownload() {
      return (
        this.hasRecordsToEdit &&
        !this.isLoadingRelevantAnnotations &&
        !this.isDownloading
      )
    },
    payload() {
      const editedRecordsInfo = this.exportSelected
        ? { ids: this.selectedRecordIds }
        : this.allRecords.queryFilters
      const exportedAttibutes = this.exportAttributes ? [] : ['name', 'id']
      const exportedAnnotations = this.exportedAnnotations?.map((a) => a.value)
      return {
        ...editedRecordsInfo,
        attribute_fields: exportedAttibutes,
        annotation_fields: exportedAnnotations || [],
      }
    },
  },
  methods: {
    getHeadingNameLabel,
    hasChangePermission(record) {
      return record?.permissions?.value?.allowed?.includes('change') || false
    },
    setExportedAnnotations() {
      this.exportedAnnotations = this.exportSelected
        ? this.selectedRecordsAnnotations
        : this.relevantAnnotations
    },
    async handleExport() {
      this.isDownloading = true
      try {
        const response = await downloadFile({
          url: this.url,
          method: 'put',
          payload: this.payload,
        })
        if (response.status === 200) {
          this.$emit('update:isOpen', false)
        }
      } catch (e) {
        this.$store.commit('notify', {
          type: 'error',
          message:
            e.response.status === 405
              ? `[405 ${e.response.statusText}] You are currently trying to download data filtered with annotations, this is not implemented yet`
              : 'Server error.',
          isDismissable: false,
          duration: 6000,
        })
      } finally {
        this.isDownloading = false
      }
    },
  },
  watch: {
    isOpen: {
      handler: function () {
        this.exportSelected = this.selectedRecordsCount > 0
        this.setExportedAnnotations()
      },
      immediate: true,
    },
    exportSelected: {
      handler: 'setExportedAnnotations',
      immediate: true,
    },
    relevantAnnotations: {
      handler: 'setExportedAnnotations',
      immediate: true,
    },
  },
}
</script>
